import type { ProfileCard } from '@shared/types/profile';
import type { AdminSearchGroup } from '../../features/admin/api/adminSearchApi';
import { type AdminSearchResult, scoreLabel } from './adminSearchMatch';

/** No subsequence tier: metadata many unrelated profiles share (a base prompt
 * naming convention, a common middleware step) should never "match" on the
 * strength of scattered letters — see scoreLabel's ScoreLabelOptions doc. */
const scoreMetadataLabel = (label: string, query: string): number =>
  scoreLabel(label, query, { allowSubsequence: false });

/**
 * A row the palette can select. Pages come from the nav config; entities come from
 * the server. Both collapse to "an icon, two lines of text, and a route".
 */
export type AdminSearchRow = {
  key: string;
  icon: string;
  title: string;
  /** Route for a page, email (or similar) for an entity. */
  subtitle: string;
  route: string;
  /** Renders the subtitle as a path rather than prose. */
  subtitleIsRoute: boolean;
};

export type AdminSearchSection = {
  key: string;
  label: string;
  rows: AdminSearchRow[];
};

/**
 * Maps an entity type to its detail route. The server deliberately does not send
 * routes, so this is the single place where a type becomes a URL.
 *
 * A type with no entry here is dropped rather than rendered dead, which is what
 * lets the server add a source before the frontend knows how to land on it.
 */
export const ADMIN_ENTITY_ROUTES: Record<string, (id: string) => string> = {
  coach: (id) => `/admin/coaches/${encodeURIComponent(id)}`,
  member: (id) => `/admin/members/${encodeURIComponent(id)}`,
};

/** Icon per entity type, matching the nav's icon for that area. */
const ENTITY_ICONS: Record<string, string> = {
  coach: 'groups',
  member: 'group',
};

export const pageSection = (
  results: readonly AdminSearchResult[],
  hasQuery: boolean
): AdminSearchSection | null => {
  if (results.length === 0) return null;
  return {
    key: 'pages',
    label: hasQuery ? 'Pages' : 'Jump to',
    rows: results.map((result) => ({
      key: `page:${result.route}`,
      icon: result.icon,
      title: result.label,
      subtitle: result.route,
      route: result.route,
      subtitleIsRoute: true,
    })),
  };
};

export const entitySections = (
  groups: readonly AdminSearchGroup[] | undefined
): AdminSearchSection[] => {
  if (!groups?.length) return [];

  const sections: AdminSearchSection[] = [];
  for (const group of groups) {
    const toRoute = ADMIN_ENTITY_ROUTES[group.type];
    if (!toRoute) continue; // server knows a type this build cannot open

    const rows = group.results.map((hit) => ({
      key: `${group.type}:${hit.id}`,
      icon: ENTITY_ICONS[group.type] ?? 'person',
      title: hit.title,
      subtitle: hit.subtitle,
      route: toRoute(hit.id),
      subtitleIsRoute: false,
    }));

    if (rows.length) {
      sections.push({ key: group.type, label: group.label, rows });
    }
  }
  return sections;
};

export const PROFILE_SEARCH_LIMIT = 5;

/**
 * Every base prompt slug is wrapped as `profile.<name>.base` — a convention shared
 * by literally every profile, so it carries no discriminating signal. Left in, it
 * actively misleads: scoring "pri" against "profile.default.base" finds a tight
 * subsequence match *inside the word "profile" itself* (p-r-·-i at positions 0,1,4),
 * so a short query matches every profile in the list regardless of what it's
 * actually named. Stripping the wrapper before scoring targets the part a person
 * would recognize.
 */
const stripBasePromptWrapper = (basePromptSlug: string): string =>
  basePromptSlug.replace(/^profile\./, '').replace(/\.base$/, '');

/**
 * Profiles are matched on the client. The list is short and the profile editor
 * already loads it, so a server round trip would buy nothing.
 *
 * Matches the slug, the base prompt, and the middleware names — the same three
 * fields `ProfileListPage` filters on, so the palette and that page agree on what
 * counts as a match. The penalties rank a slug hit above a base-prompt hit above a
 * middleware hit.
 */
export const profileSection = (
  profiles: readonly ProfileCard[] | undefined,
  query: string,
  limit: number = PROFILE_SEARCH_LIMIT
): AdminSearchSection | null => {
  const trimmed = query.trim();
  if (!trimmed || !profiles?.length) return null;

  const scored = profiles
    .map((profile) => ({
      profile,
      score: Math.max(
        scoreLabel(profile.slug, trimmed),
        scoreMetadataLabel(stripBasePromptWrapper(profile.base_prompt_slug), trimmed) - 150,
        ...profile.middleware_names.map((name) => scoreMetadataLabel(name, trimmed) - 250)
      ),
    }))
    .filter((candidate) => candidate.score > -1)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);

  if (!scored.length) return null;

  return {
    key: 'profile',
    label: 'Profiles',
    rows: scored.map(({ profile }) => ({
      key: `profile:${profile.slug}`,
      icon: 'person_edit',
      title: profile.slug,
      subtitle: profile.has_draft_edits
        ? `${profile.base_prompt_slug} · draft edits`
        : profile.base_prompt_slug,
      route: `/admin/profiles/${encodeURIComponent(profile.slug)}`,
      subtitleIsRoute: false,
    })),
  };
};

/**
 * Instant sections first, awaited ones last.
 *
 * Pages and profiles are matched locally, so they render on the first keystroke;
 * coaches and members arrive from the server a moment later. Putting the local
 * sections above the remote ones means late arrivals only ever *append* — rows
 * already on screen never shift under the cursor.
 *
 * Within that, the order is fixed rather than score-interleaved: local scores come
 * from the matcher and remote order comes from Postgres, so the two have no
 * comparable score to merge on.
 */
export const buildAdminSearchSections = (
  pages: readonly AdminSearchResult[],
  groups: readonly AdminSearchGroup[] | undefined,
  hasQuery: boolean,
  localSections: readonly (AdminSearchSection | null)[] = []
): AdminSearchSection[] => {
  const sections: AdminSearchSection[] = [];
  const page = pageSection(pages, hasQuery);
  if (page) sections.push(page);
  for (const section of localSections) {
    if (section) sections.push(section);
  }
  sections.push(...entitySections(groups));
  return sections;
};

/** Flat row order, which is what the arrow keys walk. */
export const flattenSections = (sections: readonly AdminSearchSection[]): AdminSearchRow[] =>
  sections.flatMap((section) => section.rows);
